import React, { useState, useCallback, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { createAppPlayerRequest } from '../services/api';

const EMPTY_FORM = {
  title: '',
  playStyle: '',
  positionsRequired: '',
  description: '',
  nationality: '',
  ageRange: '',
  gender: 'Any',
  foot: 'Any',
  category: '',
  language: '',
  expiresAt: '',
};

const inputStyle = { width: '100%', flex: 'none', minWidth: 0 };

function splitList(value) {
  return String(value || '').split(',').map(s => s.trim()).filter(Boolean);
}
function cleanValue(value) {
  const c = String(value || '').trim();
  return c || null;
}

const Field = React.memo(function Field({ label, children }) {
  return (
    <div>
      <label style={{ fontSize:12, color:'var(--text-dim)', display:'block', marginBottom:6, textTransform:'uppercase' }}>
        {label}
      </label>
      {children}
    </div>
  );
});

const SectionTitle = React.memo(function SectionTitle({ children }) {
  return (
    <div style={{ fontWeight:500, fontSize:13, color:'var(--text-primary)', margin:'22px 0 12px', textTransform:'uppercase' }}>
      {children}
    </div>
  );
});

const TextInput = React.memo(function TextInput({ value, onChange, placeholder, type }) {
  return (
    <input
      className="search-input"
      style={inputStyle}
      type={type || 'text'}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
    />
  );
});

const TextArea = React.memo(function TextArea({ value, onChange, placeholder, rows }) {
  return (
    <textarea
      className="search-input"
      style={{ ...inputStyle, resize:'vertical', fontFamily:'inherit', minHeight: rows ? rows * 24 + 'px' : '72px' }}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows || 3}
    />
  );
});

const SelectInput = React.memo(function SelectInput({ value, onChange, children }) {
  return (
    <select
      className="search-input"
      style={inputStyle}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {children}
    </select>
  );
});

export default function PlayerRequests({ toast }) {
  const { auth } = useApp();
  const [form, setForm]             = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated]       = useState(null);
  const [tab, setTab]               = useState('post');
  const [requests, setRequests]     = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const canSubmit = useMemo(() => (
    form.title.trim() && form.playStyle.trim() && splitList(form.positionsRequired).length > 0
  ), [form.title, form.playStyle, form.positionsRequired]);

  const setTitle       = useCallback(v => setForm(p => ({ ...p, title: v })), []);
  const setPlayStyle   = useCallback(v => setForm(p => ({ ...p, playStyle: v })), []);
  const setPositions   = useCallback(v => setForm(p => ({ ...p, positionsRequired: v })), []);
  const setDescription = useCallback(v => setForm(p => ({ ...p, description: v })), []);
  const setNationality = useCallback(v => setForm(p => ({ ...p, nationality: v })), []);
  const setAgeRange    = useCallback(v => setForm(p => ({ ...p, ageRange: v })), []);
  const setGender      = useCallback(v => setForm(p => ({ ...p, gender: v })), []);
  const setFoot        = useCallback(v => setForm(p => ({ ...p, foot: v })), []);
  const setCategory    = useCallback(v => setForm(p => ({ ...p, category: v })), []);
  const setLanguage    = useCallback(v => setForm(p => ({ ...p, language: v })), []);
  const setExpiresAt   = useCallback(v => setForm(p => ({ ...p, expiresAt: v })), []);

  const loadRequests = useCallback(async () => {
    setLoadingList(true);
    try {
      const res = await fetch('/api/app/player-requests', {
        headers: { Authorization: 'Bearer ' + (auth?.token || '') },
      });
      const d = await res.json();
      setRequests(Array.isArray(d.data) ? d.data : Array.isArray(d) ? d : []);
    } catch (e) {
      toast && toast('Failed to load requests', 'error');
    } finally {
      setLoadingList(false);
    }
  }, [auth, toast]);

  React.useEffect(() => {
    if (tab === 'list') loadRequests();
  }, [tab, loadRequests]);

  const submit = async (event) => {
    event.preventDefault();
    if (!canSubmit || submitting) return;
    const positions = splitList(form.positionsRequired);
    setSubmitting(true);
    try {
      const results = await Promise.allSettled(
        positions.map(pos =>
          createAppPlayerRequest(auth?.token, {
            title:             form.title.trim() + ' - ' + pos,
            playStyle:         form.playStyle.trim(),
            positionsRequired: [pos],
            nationality:       cleanValue(form.nationality) ? [form.nationality.trim()] : [],
            ageRange:          cleanValue(form.ageRange),
            gender:            form.gender || 'Any',
            foot:              cleanValue(form.foot),
            category:          cleanValue(form.category),
            language:          cleanValue(form.language),
            description:       cleanValue(form.description),
            expiresAt:         cleanValue(form.expiresAt),
          })
        )
      );
      const ok  = results.filter(r => r.status === 'fulfilled' && r.value && (r.value.data || r.value.id)).length;
      const bad = results.length - ok;
      if (ok > 0 && bad === 0) {
        setCreated({ count: ok, positions });
        setForm(EMPTY_FORM);
        toast && toast(ok + ' request' + (ok > 1 ? 's' : '') + ' posted! Matching players will be notified.');
      } else if (ok > 0) {
        toast && toast(ok + ' posted, ' + bad + ' failed.', 'error');
        setForm(EMPTY_FORM);
      } else {
        const msg = (results[0]?.reason?.message) || (results[0]?.value?.message) || 'Failed to post request';
        toast && toast(msg, 'error');
      }
    } catch (err) {
      toast && toast(err.message || 'Something went wrong', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = useCallback(() => { setForm(EMPTY_FORM); setCreated(null); }, []);

  return (
    <div>
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontFamily:'var(--font-display)', fontSize:22, fontWeight:500, color:'var(--text-primary)', margin:0 }}>Player Requests</h1>
        <p style={{ fontSize:13, color:'var(--text-dim)', marginTop:4 }}>Post requests visible to scouts, agents and matching players on the app</p>
      </div>

      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:24, gap:0 }}>
        {['post', 'list'].map(t => (
          <button key={t} onClick={() => { setTab(t); setCreated(null); }}
            style={{ padding:'9px 20px', fontSize:13, border:'none', cursor:'pointer', background:'none',
              color: tab===t ? 'var(--accent)' : 'var(--text-dim)',
              borderBottom: tab===t ? '2px solid var(--accent)' : '2px solid transparent',
              fontWeight: tab===t ? 600 : 400, fontFamily:'var(--font-body)', transition:'all 0.15s' }}>
            {t === 'post' ? 'Post a request' : 'My requests'}
          </button>
        ))}
      </div>

      {tab === 'post' && !created && (
        <form className="card" onSubmit={submit} autoComplete="off" style={{ marginBottom:20, padding:'20px 24px' }}>
          <div style={{ fontSize:15, fontWeight:500, color:'var(--text-primary)', marginBottom:4 }}>New player request</div>
          <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:20, lineHeight:1.55 }}>
            Each position is posted as a separate request — only players with that position see it and receive a push notification.
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Field label="Title *">
              <TextInput value={form.title} onChange={setTitle} placeholder="e.g. Seeking experienced fullback" />
            </Field>
            <Field label="Play Style *">
              <TextInput value={form.playStyle} onChange={setPlayStyle} placeholder="e.g. Attacking fullback, box-to-box" />
            </Field>
          </div>

          <SectionTitle>Player Filters</SectionTitle>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Field label="Positions Required *">
              <TextInput value={form.positionsRequired} onChange={setPositions} placeholder="Striker, Centre-Back (comma separated)" />
            </Field>
            <Field label="Nationality">
              <TextInput value={form.nationality} onChange={setNationality} placeholder="e.g. Nigeria, Ghana" />
            </Field>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Field label="Age Range">
              <TextInput value={form.ageRange} onChange={setAgeRange} placeholder="e.g. 18-25" />
            </Field>
            <Field label="Category">
              <SelectInput value={form.category} onChange={setCategory}>
                <option value="">Any category</option>
                <option value="Youth">Youth</option>
                <option value="Amateur">Amateur</option>
                <option value="Semi-Pro">Semi-Pro</option>
                <option value="Professional">Professional</option>
                <option value="Elite">Elite</option>
              </SelectInput>
            </Field>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Field label="Gender">
              <SelectInput value={form.gender} onChange={setGender}>
                <option value="Any">Any</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </SelectInput>
            </Field>
            <Field label="Foot">
              <SelectInput value={form.foot} onChange={setFoot}>
                <option value="Any">Any</option>
                <option value="Left">Left</option>
                <option value="Right">Right</option>
                <option value="Both">Both</option>
              </SelectInput>
            </Field>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
            <Field label="Language">
              <TextInput value={form.language} onChange={setLanguage} placeholder="Optional" />
            </Field>
            <Field label="Expires At">
              <input className="search-input" style={inputStyle} type="datetime-local"
                value={form.expiresAt} onChange={e => setExpiresAt(e.target.value)} />
            </Field>
          </div>

          <SectionTitle>Description</SectionTitle>
          <div style={{ marginBottom:20 }}>
            <Field label="Request Note">
              <TextArea value={form.description} onChange={setDescription}
                placeholder="Add any extra player requirements or context." rows={4} />
            </Field>
          </div>

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={reset} disabled={submitting}>Clear</button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit || submitting}>
              {submitting
                ? 'Posting...'
                : splitList(form.positionsRequired).length > 1
                  ? 'Post ' + splitList(form.positionsRequired).length + ' requests'
                  : 'Post request'}
            </button>
          </div>
        </form>
      )}

      {tab === 'post' && created && (
        <div className="card" style={{ padding:'40px 28px', textAlign:'center' }}>
          <div style={{ fontSize:36, marginBottom:12 }}>✅</div>
          <div style={{ fontSize:18, fontWeight:500, color:'var(--text-primary)', marginBottom:8 }}>
            {created.count} request{created.count > 1 ? 's' : ''} posted!
          </div>
          <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:8 }}>
            Posted for: {created.positions.join(', ')}
          </div>
          <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:24 }}>
            Players with matching profiles will receive a push notification on the app.
          </div>
          <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
            <button className="btn btn-primary" onClick={reset}>Post another</button>
            <button className="btn btn-ghost" onClick={() => setTab('list')}>View my requests</button>
          </div>
        </div>
      )}

      {tab === 'list' && (
        <div>
          {loadingList ? (
            <div style={{ padding:'48px', textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading...</div>
          ) : requests.length === 0 ? (
            <div style={{ padding:'48px', textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>
              <div style={{ fontSize:32, marginBottom:12 }}>📋</div>
              <div>No requests posted yet</div>
              <button className="btn btn-primary" onClick={() => setTab('post')} style={{ marginTop:16 }}>Post first request</button>
            </div>
          ) : (
            <div>
              <div style={{ fontSize:13, color:'var(--text-dim)', marginBottom:14 }}>
                {requests.length} request{requests.length !== 1 ? 's' : ''}
              </div>
              {requests.map(r => (
                <div key={r.id} className="card" style={{ padding:'16px 18px', marginBottom:10 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
                    <div style={{ fontSize:14, fontWeight:500, color:'var(--text-primary)' }}>{r.title}</div>
                    <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20,
                      background: r.status==='Active'?'rgba(167,244,50,0.1)':`rgba(255,80,80,0.1)`,
                      color: r.status==='Active'?'var(--accent)':'#ff6b6b',
                      border: '0.5px solid '+(r.status==='Active'?'var(--accent)':'#ff6b6b'),
                      flexShrink:0, marginLeft:8 }}>{r.status}</span>
                  </div>
                  <div style={{ fontSize:12, color:'var(--text-dim)', marginBottom:4 }}>
                    {(r.positionsRequired||[]).join(', ')}
                    {r.nationality && r.nationality.length ? ' · ' + r.nationality.join(', ') : ''}
                    {r.ageRange ? ' · Age: ' + r.ageRange : ''}
                  </div>
                  {r.playStyle && <div style={{ fontSize:12, color:'var(--text-dim)' }}>Style: {r.playStyle}</div>}
                  <div style={{ fontSize:11, color:'var(--text-dim)', opacity:.6, marginTop:5 }}>
                    Posted: {new Date(r.dateCreated||r.createdAt).toLocaleDateString()}
                    {r.expiresAt ? ' · Expires: ' + new Date(r.expiresAt).toLocaleDateString() : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
